import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { Modal, Button, Spinner, Alert } from 'react-bootstrap';
import { FaCalendarAlt, FaTrash } from 'react-icons/fa';

const API_BASE_URL = 'https://tokenly.biswaas.com/api';

const NextHoliday = () => {
  const [isLoading, setLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    const token = localStorage.getItem('tokenly_token');
    if (!token) {
      setError('No token found. Please login again.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/user/get-holiday`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      const data = await response.json();
      
      if (data.status) {
        setHolidays(Array.isArray(data.data) ? data.data : data.data ? [data.data] : []);
      } else {
        setError(data.message || 'Failed to fetch holidays');
      }
    } catch (error) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const addHoliday = async (date, action) => {
    const token = localStorage.getItem('tokenly_token');
    if (!token) {
      setError('No token found. Please login again.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/user/add-holiday`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          start_date: moment(date).format('YYYY-MM-DD'),
          action: action
        }),
      });

      const data = await response.json();
      
      if (data.status) {
        setSuccess(data.message || 'Holiday updated successfully!');
        if (action === 'add') {
          setShowDatePicker(false);
        }
        // Refresh holidays list
        setTimeout(() => {
          fetchHolidays();
        }, 1000);
      } else {
        setError(data.message || 'Failed to update holiday');
      }
    } catch (error) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    addHoliday(date, 'add');
  };

  const removeHoliday = (holiday) => {
    if (window.confirm('Are you sure you want to remove this holiday?')) {
      addHoliday(holiday.start_date, 'remove');
    }
  };

  const HolidayItem = ({ holiday, index }) => (
    <div className="d-flex align-items-center justify-content-between p-3 mb-2 bg-white rounded-3 shadow-sm">
      <div className="d-flex align-items-center flex-grow-1">
        <FaCalendarAlt className="text-primary me-3" />
        <div>
          <div className="fw-semibold text-dark">
            {moment(holiday.start_date).format('DD/MM/YYYY')}
          </div>
          <div className="text-muted small">
            {moment(holiday.start_date).format('dddd')}
          </div>
        </div>
      </div>
      <Button
        variant="outline-danger"
        size="sm"
        onClick={() => removeHoliday(holiday)}
        className="rounded-circle p-2"
        style={{ width: '40px', height: '40px' }}
      >
        <FaTrash className="mb-1" />
      </Button>
    </div>
  );

  return (
    <>
      {/* Hero Section with Background Image - Same as other pages */}
      <section className="contact-hero d-flex align-items-center">
        <div className="contact-hero-overlay"></div>
        <div className="container-fluid container-xl px-4 px-md-3">
          <h1 className="display-5 fw-bold text-white mb-1">Holiday <span className="text-warning">Management</span></h1>
          <p className="text-light mb-0">
            Manage your clinic holidays and non-working days
          </p>
        </div>
      </section>

      <div className="container-xl mt-4">
        <div className="row justify-content-center">
          <div className="col-md-8">
            <div className="card shadow-lg border-0 overflow-hidden" style={{
              borderRadius: "24px",
              backgroundColor: "#ffffff",
              boxShadow: '0 20px 50px rgba(0,0,0,0.12) !important'
            }}>
              <div className="card-header text-white" style={{
                background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                borderRadius: '24px 24px 0 0',
                border: 'none',
                padding: '1.5rem'
              }}>
                <h4 className="mb-0">Holiday Management</h4>
              </div>
              <div className="card-body p-4">
                {/* Alerts */}
                {error && (
                  <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                  </Alert>
                )}
                {success && (
                  <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                    {success}
                  </Alert>
                )}

                {/* Add Holiday Button */}
                <div className="text-center mb-4">
                  <Button
                    variant="success"
                    size="lg"
                    onClick={() => setShowDatePicker(true)}
                    className="px-4 py-2 rounded-pill"
                    disabled={isLoading}
                  >
                    <FaCalendarAlt className="me-2" />
                    Add Holiday
                  </Button>
                </div>

                {/* Holidays List */}
                <div className="holidays-list">
                  {isLoading ? (
                    <div className="text-center py-5">
                      <Spinner animation="border" variant="primary" />
                      <p className="mt-3 text-muted">Loading holidays...</p>
                    </div>
                  ) : holidays.length > 0 ? (
                    <div>
                      <h5 className="mb-3 text-muted">Upcoming Holidays</h5>
                      {holidays.map((holiday, index) => (
                        <HolidayItem key={holiday.id || index} holiday={holiday} index={index} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <FaCalendarAlt className="text-muted mb-3" style={{ fontSize: '3rem' }} />
                      <h5 className="text-muted">No holidays added</h5>
                      <p className="text-muted">Click "Add Holiday" to add your first holiday</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Date Picker Modal */}
      <Modal
        show={showDatePicker}
        onHide={() => setShowDatePicker(false)}
        centered
        backdrop="static"
        size="md"
      >
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="w-100 text-center">
            <FaCalendarAlt className="me-2 text-primary" />
            Select Holiday Date
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          <div className="mb-4">
            <input
              type="date"
              className="form-control form-control-lg text-center"
              min={moment().format('YYYY-MM-DD')}
              max="2026-11-20"
              value={moment(selectedDate).format('YYYY-MM-DD')}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              style={{ fontSize: '1.2rem' }}
            />
          </div>
          <div className="text-muted small">
            Selected: {moment(selectedDate).format('DD/MM/YYYY (dddd)')}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0 justify-content-center">
          <Button
            variant="secondary"
            onClick={() => setShowDatePicker(false)}
            className="px-4"
          >
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={() => handleDateSelect(selectedDate)}
            disabled={isLoading}
            className="px-4"
          >
            {isLoading ? (
              <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              <>
                <FaCalendarAlt className="me-2" />
                Add Holiday
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default NextHoliday;